import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { searchAllocationsByWindow, updateAllocations } from '../services/api.ts';
import { localDateToUtcStart, localDateToUtcEnd, utcToLocalTime } from '../utils/timezone.ts';
import { useDayBundle } from '../data/queries.ts';
import DataFreshnessNote from './DataFreshnessNote.tsx';
import { useWorkingDate } from '../contexts/WorkingDateContext.tsx';
import { matchesNameQuery } from '../utils/nameFilter.ts';
import { NAMEPLATE_COLORS, needsNerveBlock } from './staffNameplates.ts';
import { matchesPaletteColor } from './nameplatePalette.ts';
import { colorForHospital, darkenHex } from './hospitalColors.ts';
import { loadSiteLocks, setSiteLock } from './siteLocks.ts';
import { loadRoomTransfers, setRoomTransfer, effectiveDoctorIdsForRoom, groupTransferCandidates } from './roomTransfers.ts';
import { loadNurseTransfers, effectiveNurseIdsForRoom } from './nurseTransfers.ts';
import { loadReliefOverlay, handoffTimesFor, type ReliefOverlay } from './reliefOverlay.ts';
import { appendMove } from './moveLog.ts';
import { buildDoctorEditPayload, editsForRooms, editsFromUndo, DoctorEdit, buildNurseEditPayload, nurseEditsFromUndo, NurseUndoChange, DoctorUndoChange } from './doctorMoves.ts';
import { pushUndo, popUndo, loadUndoStack, MoveUndoEntry } from './moveUndo.ts';
import { commitAllocationEdit, editedScheduleIds, GuardableAllocation } from './allocationWriteGuard.ts';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { peakConcurrentSignatures } from './supervisionUtils.ts';
import EmployeeDetailsPopover from './EmployeeDetailsPopover.tsx';
import { OffLog, recordOffToggle, setOffEntryTime, hmNow } from './offTimes.ts';
import type { DoctorDayStatus, SiteCoinOption } from './EmployeeDetailsPopover.tsx';
import { normalizeDoctorStatuses } from './EmployeeDetailsPopover.tsx';
import LockToggle from './LockToggle.tsx';
import { buildRecolorPayload } from './recolor.ts';
import type { Employee } from '../services/api.ts';
import { takeoverScheduleIds, reportInMs, reorderedPeckingIds } from './boardViewHelpers.ts';

// ---------------------------------------------------------------------------
// Anesthesiologists view (Manpower tab → "Anesthesiologists"; formerly its own tab at /anesthesiologists).
//
// A running list of every anesthesiologist working the selected day, in the SAME order as the Board
// bullpen: Fixed-Shift docs (grouped by shift window, sorted by end then start), then Pecking-Order
// (Priority) docs numbered by shift.order. Shows in-time (report-in = first covered case − 30 min, or
// − 45 min if that case needs a nerve block), out-time, assigned site(s), and left-edge color tags
// (meeting / request / supervising / solo).
//
// EDITING: the list starts LOCKED (read-only). Unlock to edit; it auto-locks after 30s of no changes,
// or lock manually. When unlocked you can: reorder pecking-order docs by dragging (the new order is
// mirrored into the Board bullpen), edit the in-time, and write a note in the bar under each name. From
// each doctor's card you can also set a disposition — Meeting / Request case / Off (sent home) — which
// tags the row and (for meeting/request) drops the typed notes under their name. Day-annotations persist
// locally per day (survive refresh).
// NOTE: no North/South/Children's zones in this version (per product owner) — grouped by section only.
// ---------------------------------------------------------------------------

interface EmployeeLike { employeeId: string; employeeType: string; firstName: string; lastName: string; homeHospitalId?: string; credentials?: string[]; }
interface ShiftLike { employeeId: string; startTimeUtc?: string; endTimeUtc?: string; order?: number | null; }
interface HospitalLike { hospitalId: string; name: string; color?: string; }
interface RoomLike { operatingRoomId: string; hospitalId: string; name?: string; roomNumber?: string; credential?: string; }
interface ScheduleLike { scheduleId: string; operatingRoomId: string; startTimeUtc: string; endTimeUtc: string; surgeryType?: string; }
interface AllocationLike { operatingRoomScheduleId: string; allocatedDoctorIds?: string[]; allocatedNurseIds?: string[]; colorOverrides?: Record<string, string>; }

// Per-day annotations the chief adds on this page (kept local; also read by the Board bullpen for order).
// `meeting` is the LEGACY boolean, now folded into `status` ('meeting' | 'request' | 'off') but still
// written so anything else that reads it keeps working.
interface Anno { order: Record<string, number>; notes: Record<string, string>; meeting: Record<string, boolean>; inTime: Record<string, string>; status: Record<string, DoctorDayStatus[]>; offLog: OffLog; }
const EMPTY_ANNO: Anno = { order: {}, notes: {}, meeting: {}, inTime: {}, status: {}, offLog: {} };
export const anesAnnoKey = (date: string) => `oc-anes-${date}`;
const loadAnno = (date: string): Anno => {
  try {
    const raw = localStorage.getItem(anesAnnoKey(date));
    if (raw) {
      const parsed = JSON.parse(raw);
      const a: Anno = { ...EMPTY_ANNO, ...parsed };
      // Normalize every stored disposition to a LIST (legacy single value -> [value]), then fold the legacy
      // boolean `meeting` flag into that list. Dispositions are multi-select (Meeting/Request/Off can coexist).
      const status: Record<string, DoctorDayStatus[]> = {};
      for (const id of Object.keys(parsed.status || {})) { const list = normalizeDoctorStatuses(parsed.status[id]); if (list.length) status[id] = list; }
      for (const id of Object.keys(a.meeting || {})) { if (a.meeting[id] && !(status[id] || []).includes('meeting')) status[id] = [...(status[id] || []), 'meeting']; }
      a.status = status;
      return a;
    }
  } catch { /* ignore */ }
  return EMPTY_ANNO;
};

const BENCH_IN_HM = '11:30';           // a pecking-order doc we don't need comes in at the 11:30 bench (Braden 8/5)
const MEETING_COLOR = '#4a2c10';       // dark brown — "has a meeting today" tag (stands out on orange)
const OFF_COLOR = '#6b7280';           // slate gray — "sent home / off"
const AUTO_LOCK_MS = 30000;            // auto-lock after 30s of no edits

// "HH:MM" (24h local) -> "6:30a" style.
const to12h = (hhmm: string): string => {
  if (!hhmm || hhmm.length < 4) return hhmm || '';
  let h = parseInt(hhmm.slice(0, 2), 10);
  const m = hhmm.slice(3, 5);
  const ap = h >= 12 ? 'p' : 'a';
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${m}${ap}`;
};
const localHM = (utc?: string): string => (utc ? utcToLocalTime(utc) : '');
const localMin = (utc?: string): number => { const t = localHM(utc); return t ? (+t.slice(0, 2)) * 60 + (+t.slice(3, 5)) : 99999; };
// Manual in-time roller options: every 15 min from 05:00 to 23:00 (local "HH:MM").
const IN_TIME_GRID: string[] = (() => {
  const out: string[] = [];
  for (let m = 5 * 60; m <= 23 * 60; m += 15) out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  return out;
})();

// A short site tag from a hospital name (e.g. "Northwestern SC" -> "NWSC", "Big Main Hospital" -> "BMH").
const siteTag = (name?: string): string => {
  if (!name) return '';
  const words = name.replace(/[’']/g, '').split(/\s+/).filter(Boolean);
  const letters = words.map(w => (w.toUpperCase() === 'SC' ? 'SC' : w[0].toUpperCase())).join('');
  return letters.slice(0, 5);
};

export interface StaffDayViewProps {
  /** Rendered inside the Manpower page, which owns the heading and the shared date picker. */
  embedded?: boolean;
  /** Show only people whose name matches (see utils/nameFilter.ts). Editing still acts on the full list. */
  nameFilter?: string;
}

const AnesthesiologistsView: React.FC<StaffDayViewProps> = ({ embedded = false, nameFilter = '' }) => {
  // RBAC is enforced server-side (PUT /allocations is withPaidAdmin), but this tab writes allocations, so it
  // must not offer edit controls it knows will 403 — a silently-rejected move would leave the runner reading a
  // board the server never agreed to. Mirrors BoardView's isAdmin gating.
  const { isAdmin } = useAuth();
  // The shared working date (same day as the Board and the rest of Manpower).
  const [selectedDate, setSelectedDate] = useWorkingDate();
  // The day's data comes from the shared cache (data/queries.ts), so switching between the Board and the
  // Manpower views — or back to a day already viewed — renders without refetching. Allocation edits are
  // written into that same cache entry, so every screen agrees on them immediately.
  const day = useDayBundle(selectedDate);
  const employees = day.employees as unknown as EmployeeLike[];
  const shifts = day.shifts as ShiftLike[];
  const hospitals = day.hospitals as unknown as HospitalLike[];
  const rooms = day.operatingRooms as unknown as RoomLike[];
  const schedules = day.schedules as unknown as ScheduleLike[];
  const allocations = day.allocations as AllocationLike[];
  const setAllocations = day.setAllocations;
  const loading = day.coreLoading;
  // Whether `allocations` reflects a SUCCESSFUL fetch. An empty list from a failed request
  // is indistinguishable from a genuinely empty day, and PUT /allocations rewrites the whole
  // window — so no writer may run until this is true. See handleRecolor.
  const allocationsLoaded = day.allocationsLoaded;
  // Write errors from this tab; load failures are reported from the cache state below.
  const [error, setError] = useState('');
  const loadError = day.coreError ? 'Failed to load data' : '';
  useEffect(() => { if (day.allocationsError) console.error('allocations fetch failed', day.allocationsError); }, [day.allocationsError]);

  // Editing / lock state.
  const [locked, setLocked] = useState(true);
  const [anno, setAnno] = useState<Anno>(() => loadAnno(selectedDate));
  const [siteLocks, setSiteLocks] = useState<Record<string, string>>(() => loadSiteLocks(selectedDate));
  const [roomTransfers, setRoomTransfers] = useState<Record<string, string>>(() => loadRoomTransfers(selectedDate));
  // The CRNA half of the same overlay. This tab does not EDIT nurse transfers (the Board does), but it must
  // READ them, or a room whose CRNA was moved on the Board still reads as supervised-by-the-old-CRNA here —
  // and the person card's signature dots then disagree with the Board's for the same doctor.
  const [nurseTransfers, setNurseTransfers] = useState<Record<string, string>>(() => loadNurseTransfers(selectedDate));
  // The Board's relief boxes — for the handoff time of a room someone takes over partway through a case.
  const [reliefOverlay, setReliefOverlay] = useState<ReliefOverlay>(() => loadReliefOverlay(selectedDate));
  const [undoStack, setUndoStack] = useState<MoveUndoEntry[]>(() => loadUndoStack(selectedDate));
  const [dragId, setDragId] = useState<string | null>(null);
  // Open site-lock picker (a floating, scrollable list of color-coded site coins), anchored to a coin.
  const [siteLockOpen, setSiteLockOpen] = useState<{ id: string; top: number; left: number } | null>(null);
  // Which anesthesiologists have their "Nerve block" detail panel expanded (click the chip to toggle).
  const [nbOpenIds, setNbOpenIds] = useState<Set<string>>(new Set());
  const toggleNbOpen = (id: string) => setNbOpenIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const lockTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reload annotations + site locks + re-lock whenever the day changes.
  useEffect(() => { setAnno(loadAnno(selectedDate)); setSiteLocks(loadSiteLocks(selectedDate)); setRoomTransfers(loadRoomTransfers(selectedDate)); setNurseTransfers(loadNurseTransfers(selectedDate)); setReliefOverlay(loadReliefOverlay(selectedDate)); setUndoStack(loadUndoStack(selectedDate)); setLocked(true); setNbOpenIds(new Set()); setSiteLockOpen(null); }, [selectedDate]);
  // Re-read the shared overlays (dispositions, site locks, room transfers) when the window regains focus, so a
  // change made on the Board tab shows here after switching back (mirrors the Board's focus reload).
  useEffect(() => {
    const reload = () => { setAnno(loadAnno(selectedDate)); setSiteLocks(loadSiteLocks(selectedDate)); setRoomTransfers(loadRoomTransfers(selectedDate)); setNurseTransfers(loadNurseTransfers(selectedDate)); setReliefOverlay(loadReliefOverlay(selectedDate)); setUndoStack(loadUndoStack(selectedDate)); };
    window.addEventListener('focus', reload);
    return () => window.removeEventListener('focus', reload);
  }, [selectedDate]);
  // Can this provider actually work at a site? True if the site has any open room (no credential) or a room whose
  // required credential they hold. Used to WARN before locking someone somewhere they can't be placed.
  const canWorkAtSite = (empId: string, hospitalId: string): boolean => {
    const creds = new Set(employees.find(e => e.employeeId === empId)?.credentials || []);
    const roomsAtH = rooms.filter(r => r.hospitalId === hospitalId);
    return roomsAtH.length > 0 && roomsAtH.some(r => !r.credential || creds.has(r.credential));
  };
  // Set/clear a hard site lock (used on the next Allocate run to confine this doctor to that site). Warn first if
  // they aren't credentialed there — a hard lock to a site they can't work leaves them UNPLACED.
  const setLock = (empId: string, hospitalId: string | null) => {
    if (hospitalId && !canWorkAtSite(empId, hospitalId)) {
      const emp = employees.find(e => e.employeeId === empId);
      const hname = hospitals.find(h => h.hospitalId === hospitalId)?.name || hospitalId;
      if (!window.confirm(`${emp ? `${emp.firstName} ${emp.lastName}` : 'This provider'} is not credentialed at ${hname}, so a hard lock there would leave them UNSTAFFED for the day. Lock them anyway?`)) return;
    }
    setSiteLocks(setSiteLock(selectedDate, empId, hospitalId)); bumpAutoLock();
  };
  useEffect(() => () => { if (lockTimer.current) clearTimeout(lockTimer.current); }, []);

  // Any edit (or unlocking) resets the 30-second auto-lock countdown.
  const bumpAutoLock = () => {
    if (lockTimer.current) clearTimeout(lockTimer.current);
    lockTimer.current = setTimeout(() => setLocked(true), AUTO_LOCK_MS);
  };
  // Every mutating control on this tab sits behind `locked`, so refusing to unlock for a read-only account
  // gates the whole editing surface in one place (the server refuses the writes too — this stops the runner
  // from making moves that would be silently rejected).
  const unlock = () => { if (!isAdmin) return; setLocked(false); bumpAutoLock(); };
  const lockNow = () => { if (lockTimer.current) clearTimeout(lockTimer.current); setLocked(true); };

  // Persist an annotation change and keep the edit window alive.
  const mutateAnno = (fn: (prev: Anno) => Anno) => {
    setAnno(prev => {
      const next = fn(prev);
      try { localStorage.setItem(anesAnnoKey(selectedDate), JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    bumpAutoLock();
  };
  // Set (or clear) the chief's disposition for a doctor: 'meeting' | 'request' | 'off' | null. Keeps the
  // legacy `meeting` boolean in sync so nothing else that still reads it breaks.
  const toggleStatus = (id: string, status: DoctorDayStatus) => mutateAnno(p => {
    const nextStatus = { ...p.status };
    const cur = normalizeDoctorStatuses(nextStatus[id]);
    const nextList = cur.includes(status) ? cur.filter(x => x !== status) : [...cur, status];
    if (nextList.length) nextStatus[id] = nextList; else delete nextStatus[id];
    // Setting Off records when they were sent home; clearing it records when they were called back (offTimes.ts).
    const offLog = status === 'off' ? recordOffToggle(p.offLog, id, !cur.includes('off'), hmNow()) : p.offLog;
    return { ...p, status: nextStatus, offLog, meeting: { ...p.meeting, [id]: nextList.includes('meeting') } };
  });
  const setNote = (id: string, text: string) => mutateAnno(p => ({ ...p, notes: { ...p.notes, [id]: text } }));
  const setOffTime = (id: string, index: number, field: 'off' | 'back', hm: string) =>
    mutateAnno(p => ({ ...p, offLog: setOffEntryTime(p.offLog, id, index, field, hm) }));
  const setInTime = (id: string, hhmm: string) => mutateAnno(p => {
    const inTime = { ...p.inTime }; if (hhmm) inTime[id] = hhmm; else delete inTime[id];
    return { ...p, inTime };
  });

  // Clear a previous day's write error when the day changes.
  useEffect(() => { setError(''); }, [selectedDate]);

  // ---- Derived lookups ----
  const hospitalNameById = new Map(hospitals.map(h => [h.hospitalId, h.name]));
  const hospitalById = new Map(hospitals.map(h => [h.hospitalId, h]));
  const hospitalByName = new Map(hospitals.map(h => [h.name, h]));
  const hospitalNames = hospitals.map(h => h.name);
  const roomById = new Map(rooms.map(r => [r.operatingRoomId, r]));
  const scheduleById = new Map(schedules.map(s => [s.scheduleId, s]));
  const empTypeById = new Map(employees.map(e => [e.employeeId, e.employeeType]));

  // Apply the shared room-transfer overlay (oc-room-transfers-<date>) so a room handed to another doctor — on
  // the Board OR here — reports the target doctor everywhere. Mirrors BoardView.effectiveDoctorIds.
  const effectiveDoctorIds = (a: AllocationLike): string[] =>
    effectiveDoctorIdsForRoom(a.allocatedDoctorIds || [], scheduleById.get(a.operatingRoomScheduleId)?.operatingRoomId, roomTransfers);
  // The CRNA analogue, mirroring BoardView.effectiveNurseIds. Everything that asks "is this room supervised,
  // and by which CRNA" must go through here so this tab and the Board answer identically.
  const isCrnaId = (id: string): boolean => empTypeById.get(id) === 'nurse';
  const effectiveNurseIds = (a: AllocationLike): string[] =>
    effectiveNurseIdsForRoom(a.allocatedNurseIds || [], scheduleById.get(a.operatingRoomScheduleId)?.operatingRoomId, nurseTransfers, isCrnaId);
  // Schedule ids (cases) for a room, and for a doctor's EFFECTIVE rooms — the unit a doctor edit acts on.
  const scheduleIdsForRoom = (operatingRoomId: string): string[] =>
    allocations.filter(a => scheduleById.get(a.operatingRoomScheduleId)?.operatingRoomId === operatingRoomId).map(a => a.operatingRoomScheduleId);
  const scheduleIdsForDoctor = (docId: string): string[] =>
    allocations.filter(a => effectiveDoctorIds(a).includes(docId)).map(a => a.operatingRoomScheduleId);
  // Option B: a move/swap now REWRITES the allocation (persisted, no overlay) so every view agrees, records an
  // Undo entry, and clears any stale doctor overlay on the touched rooms. Guarded — buildDoctorEditPayload
  // returns null when a write would erase the day (allocations not loaded / empty / no-op).
  // Re-read the window, refuse on a genuine conflict, rebase onto fresh server state, then write. Local side
  // effects (overlay clearing, Undo entry, Move Log) happen ONLY after the write lands, so a refused or failed
  // write leaves no trace — the board is put back exactly as it was and the runner is told why.
  const persistAllocationEdit = async (
    edits: { scheduleId: string }[],
    rebuild: (source: any[]) => { payload: any[]; nextAllocations: any[]; undo: any[] } | null,
    kind: 'doctor' | 'nurse',
    undoLabel: string,
    onCommitted: (undoChanges: any[]) => void,
  ) => {
    if (!isAdmin) return;
    const res = rebuild(allocations);
    if (!res) return;
    const priorAllocations = allocations;
    setAllocations(res.nextAllocations as AllocationLike[]);
    setError('');

    const windowStart = localDateToUtcStart(selectedDate);
    const windowEnd = localDateToUtcEnd(selectedDate);
    const editedIds = editedScheduleIds(edits);
    const outcome = await commitAllocationEdit({
      snapshot: priorAllocations as unknown as GuardableAllocation[],
      editedIds,
      fetchFresh: async () => ((await searchAllocationsByWindow(windowStart, windowEnd)).allocations || []) as unknown as GuardableAllocation[],
      rebuild: (fresh) => rebuild(fresh as any[]),
      put: (payload) => updateAllocations(payload, windowStart, windowEnd),
    });

    if (!outcome.ok) {
      setAllocations(priorAllocations);                       // never show an edit the server did not accept
      if (outcome.message) setError(outcome.message);
      if (outcome.reason === 'error') console.error('allocation save failed', (outcome as any).error);
      return;
    }

    let after: AllocationLike[] = res.nextAllocations as AllocationLike[];
    try { after = ((await searchAllocationsByWindow(windowStart, windowEnd)).allocations || []) as AllocationLike[]; } catch { /* write landed; keep the optimistic view */ }
    setAllocations(after);
    setUndoStack(pushUndo(selectedDate, {
      label: undoLabel,
      kind,
      changes: res.undo,
      expected: (after as unknown as GuardableAllocation[]).filter(a => editedIds.includes(a.operatingRoomScheduleId)),
    }));
    onCommitted(res.undo);
  };

  const persistDoctorEdits = async (edits: DoctorEdit[], undoLabel: string, logDetail: string, logExtra: Record<string, any> = {}) => {
    await persistAllocationEdit(
      edits,
      (source) => buildDoctorEditPayload(source, edits, source === allocations ? allocationsLoaded : true),
      'doctor',
      undoLabel,
      (undoChanges) => {
        let nextTransfers = roomTransfers;
        for (const u of undoChanges) { const rid = scheduleById.get(u.scheduleId)?.operatingRoomId; if (rid) nextTransfers = setRoomTransfer(selectedDate, rid, null); }
        setRoomTransfers(nextTransfers);
        appendMove(selectedDate, { kind: 'transfer', detail: logDetail, ...logExtra });
      },
    );
  };
  const undoLastMove = async () => {
    if (!isAdmin) return;
    const { entry, stack } = popUndo(selectedDate);
    if (!entry) return;
    const edits = entry.kind === 'nurse'
      ? nurseEditsFromUndo(entry.changes as NurseUndoChange[])
      : editsFromUndo(entry.changes as DoctorUndoChange[]);
    const rebuild = (source: any[]) => (entry.kind === 'nurse'
      ? buildNurseEditPayload(source, edits as any, source === allocations ? allocationsLoaded : true)
      : buildDoctorEditPayload(source, edits as any, source === allocations ? allocationsLoaded : true));
    // `changes` holds ABSOLUTE prior ids, so replaying them onto a day that has moved on (a wipe, a re-allocate,
    // another runner) is an overwrite, not an undo. `expected` is what this move left behind — if the server no
    // longer matches it, the move has been superseded and the guard refuses. Legacy entries have no `expected`
    // and fall back to the ordinary stale-write check against the current snapshot.
    const snapshot = (entry.expected && entry.expected.length ? entry.expected : (allocations as unknown as GuardableAllocation[]));
    const res = rebuild(allocations);
    if (!res) { setUndoStack(stack); return; }
    const priorAllocations = allocations;
    setAllocations(res.nextAllocations as AllocationLike[]);
    setError('');

    const windowStart = localDateToUtcStart(selectedDate);
    const windowEnd = localDateToUtcEnd(selectedDate);
    const outcome = await commitAllocationEdit({
      snapshot,
      editedIds: editedScheduleIds(edits as { scheduleId: string }[]),
      fetchFresh: async () => ((await searchAllocationsByWindow(windowStart, windowEnd)).allocations || []) as unknown as GuardableAllocation[],
      rebuild: (fresh) => rebuild(fresh as any[]),
      put: (payload) => updateAllocations(payload, windowStart, windowEnd),
    });

    if (!outcome.ok) {
      setAllocations(priorAllocations);
      // The entry stays on the stack: the undo did NOT happen, so the runner can retry after reloading.
      if (outcome.reason === 'conflict') setError(`That move can no longer be undone — ${entry.label} has been superseded by a newer change. Reload to see the latest board.`);
      else if (outcome.message) setError(outcome.message);
      if (outcome.reason === 'error') console.error('undo save failed', (outcome as any).error);
      return;
    }
    setUndoStack(stack);
    appendMove(selectedDate, { kind: 'transfer', detail: `Undid: ${entry.label} (Manpower · Anesthesiologists)` });
    try { setAllocations(((await searchAllocationsByWindow(windowStart, windowEnd)).allocations || []) as AllocationLike[]); } catch { /* write landed */ }
  };
  const moveRoom = (operatingRoomId: string, toDoctorId: string) => {
    persistDoctorEdits(editsForRooms(scheduleIdsForRoom(operatingRoomId), toDoctorId),
      `Moved ${roomLabelOf(operatingRoomId)} to ${nameOfDoc(toDoctorId)}`,
      `Moved ${roomLabelOf(operatingRoomId)} to ${nameOfDoc(toDoctorId)} (Manpower · Anesthesiologists)`,
      { who: nameOfDoc(toDoctorId), room: roomLabelOf(operatingRoomId) });
  };
  const moveAllRooms = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    const scheds = scheduleIdsForDoctor(fromId);
    if (!scheds.length) return;
    persistDoctorEdits(editsForRooms(scheds, toId),
      `Transferred all of ${nameOfDoc(fromId)}'s rooms to ${nameOfDoc(toId)}`,
      `Transferred all of ${nameOfDoc(fromId)}'s rooms to ${nameOfDoc(toId)} (Manpower · Anesthesiologists)`,
      { who: nameOfDoc(toId), from: nameOfDoc(fromId) });
  };
  const swapAllRooms = (aId: string, bId: string) => {
    if (aId === bId) return;
    const aScheds = scheduleIdsForDoctor(aId), bScheds = scheduleIdsForDoctor(bId);
    if (!aScheds.length && !bScheds.length) return;
    persistDoctorEdits([...editsForRooms(aScheds, bId), ...editsForRooms(bScheds, aId)],
      `Swapped all rooms between ${nameOfDoc(aId)} and ${nameOfDoc(bId)}`,
      `Swapped all rooms between ${nameOfDoc(aId)} and ${nameOfDoc(bId)} (Manpower · Anesthesiologists)`,
      { who: nameOfDoc(aId), from: nameOfDoc(bId) });
  };
  // ---- Transfer/swap targets: ANY AVAILABLE anesthesiologist (mirrors the Board) ----
  // Everyone on shift today who isn't marked off, whether or not they hold a room right now — so a doctor's
  // rooms can be handed to an available (bullpen) doc, not just to doctors already at the site. The transfer is
  // a pure overlay (room -> target doctor id), so any doctor is a valid target.
  const workingDocIds = new Set(shifts.map((s: any) => s.employeeId));
  const transferTargetDoctorIds = employees
    .filter((e: any) => e.employeeType === 'doctor' && workingDocIds.has(e.employeeId) && !(anno.status[e.employeeId] || []).includes('off'))
    .map((e: any) => e.employeeId);
  const nameOfDoc = (did: string) => { const e = employees.find(x => x.employeeId === did); return e ? `${e.firstName} ${e.lastName}` : 'someone'; };
  const roomLabelOf = (rid?: string) => { const r = rid ? roomById.get(rid) : undefined; return (r as any)?.name || (r as any)?.roomNumber || rid || 'a room'; };
  const toCandidateList = (ids: string[], excludeId: string) => ids
    .filter(d => d !== excludeId)
    .map(d => ({ id: d, name: nameOfDoc(d) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const roomCandidatesFor = (_operatingRoomId: string, excludeId: string) => toCandidateList(transferTargetDoctorIds, excludeId);
  const allCandidatesFor = (docId: string) => toCandidateList(transferTargetDoctorIds, docId);
  // Which doctors are currently assigned at each site (effective rooms + hard site locks) — used ONLY to GROUP
  // the transfer/swap dropdown by site (the candidate pool itself is every available doctor). Mirrors the Board.
  const assignedDoctorsBySite = new Map<string, Set<string>>();
  for (const a of allocations) {
    const rid = scheduleById.get(a.operatingRoomScheduleId)?.operatingRoomId;
    const hid = rid ? roomById.get(rid)?.hospitalId : undefined;
    if (!hid) continue;
    for (const did of effectiveDoctorIds(a)) {
      if (empTypeById.get(did) !== 'doctor') continue;
      if (!assignedDoctorsBySite.has(hid)) assignedDoctorsBySite.set(hid, new Set());
      assignedDoctorsBySite.get(hid)!.add(did);
    }
  }
  Object.entries(siteLocks).forEach(([empId, hid]) => {
    if (empTypeById.get(empId) !== 'doctor') return;
    if (!assignedDoctorsBySite.has(hid)) assignedDoctorsBySite.set(hid, new Set());
    assignedDoctorsBySite.get(hid)!.add(empId);
  });
  // Swap ONE room with another doctor: hand this room to B, take B's EARLIEST room at the same site in return.
  const swapRoom = (operatingRoomId: string, fromDoctorId: string, withDoctorId: string) => {
    if (fromDoctorId === withDoctorId) return;
    const hid = roomById.get(operatingRoomId)?.hospitalId;
    const bRoomStart = new Map<string, string>();
    for (const a of allocations) {
      if (!effectiveDoctorIds(a).includes(withDoctorId)) continue;
      const sched = scheduleById.get(a.operatingRoomScheduleId);
      const rid = sched?.operatingRoomId;
      if (!rid || rid === operatingRoomId || roomById.get(rid)?.hospitalId !== hid) continue;
      const st = sched?.startTimeUtc || '';
      if (!bRoomStart.has(rid) || st < bRoomStart.get(rid)!) bRoomStart.set(rid, st);
    }
    const bEarliest = [...bRoomStart.entries()].sort((x, y) => x[1].localeCompare(y[1]))[0]?.[0];
    const edits = editsForRooms(scheduleIdsForRoom(operatingRoomId), withDoctorId);
    if (bEarliest) edits.push(...editsForRooms(scheduleIdsForRoom(bEarliest), fromDoctorId));
    persistDoctorEdits(edits,
      `Swapped ${roomLabelOf(operatingRoomId)}: ${nameOfDoc(fromDoctorId)} ⇄ ${nameOfDoc(withDoctorId)}`,
      `Swapped ${roomLabelOf(operatingRoomId)}: ${nameOfDoc(fromDoctorId)} ⇄ ${nameOfDoc(withDoctorId)} (Manpower · Anesthesiologists)`,
      { who: nameOfDoc(withDoctorId), from: nameOfDoc(fromDoctorId), room: roomLabelOf(operatingRoomId) });
  };

  // Recolor a doctor's coin/nameplate. Like BoardView.handleRecolor: apply the override to EVERY room the
  // doctor is on, then persist the FULL window (the update-allocations endpoint deletes-and-rewrites the window,
  // so we must send every allocation, not just the touched ones) so the Board reflects it on its next load.
  const handleRecolor = async (employeeId: string, color: string | null) => {
    if (!isAdmin) return;
    // buildRecolorPayload returns null when writing would be unsafe (allocations not
    // successfully loaded, list empty, or this doctor on no allocation). Since
    // PUT /allocations rewrites the entire window, writing in any of those states
    // would delete the day. See components/recolor.ts.
    const result = buildRecolorPayload(allocations, employeeId, color, allocationsLoaded);
    if (!result) return;
    const { nextAllocations: next, payload } = result;
    const priorAllocations = allocations;
    setAllocations(next as AllocationLike[]);
    try {
      await updateAllocations(payload, localDateToUtcStart(selectedDate), localDateToUtcEnd(selectedDate));
    } catch (e) {
      setAllocations(priorAllocations);            // don't leave a color the server never stored
      setError('Failed to save the color change.');
      console.error('recolor save failed', e);
    }
  };

  // A colored site "coin" descriptor (acronym on the hospital's own color) for the popover card.
  const coinFor = (hid: string): SiteCoinOption => {
    const h = hospitalById.get(hid);
    const name = h?.name || '';
    return { hospitalId: hid, name, acronym: siteTag(name), color: colorForHospital(h) };
  };
  // The doctor's credentialed REAL sites as coins (creds that map to a current hospital name), matching the board.
  const credentialCoinsFor = (e: EmployeeLike): SiteCoinOption[] =>
    // C1 phase 2: a credential may be a hospitalId (migrated) OR a hospital name (legacy) — resolve id first, then name.
    (e.credentials || []).map(c => hospitalById.get(c) ?? hospitalByName.get(c)).filter((h): h is HospitalLike => !!h)
      .map(h => ({ hospitalId: h.hospitalId, name: h.name, acronym: siteTag(h.name), color: colorForHospital(h) }));

  const shiftByEmp = new Map<string, ShiftLike>();
  for (const s of shifts) {
    const cur = shiftByEmp.get(s.employeeId);
    if (!cur || (s.startTimeUtc || '') < (cur.startTimeUtc || '')) shiftByEmp.set(s.employeeId, s);
  }
  const shiftOrderOf = (id: string): number | null => {
    const o = shiftByEmp.get(id)?.order;
    return o === undefined || o === null ? null : o;
  };
  // Effective pecking number = manual reorder override if present, else the shift's order.
  const orderOf = (id: string): number | null => (anno.order[id] ?? shiftOrderOf(id));

  const workingIds = new Set(shifts.map(s => s.employeeId));
  const doctors = employees.filter(e => e.employeeType === 'doctor' && workingIds.has(e.employeeId));

  // Bulk "back to Auto": drop EVERY manual in-time override so each doctor falls back to their derived report-in
  // (30 min before their first case; the pecking waterfall). One click instead of hitting Auto per doctor — the
  // fix for hand-laddering in-times up the pecking order, and for clearing a stale override left by a prior sim.
  const resetInTimes = () => mutateAnno(p => {
    const inTime = { ...p.inTime };
    for (const d of doctors) delete inTime[d.employeeId];
    return { ...p, inTime };
  });

  // Supervising-vs-solo is a SNAPSHOT of a single moment, NOT a whole-day roll-up. A doctor who supervises
  // CRNAs in the morning and then takes a room solo later is "supervising" at the start of the day and
  // "solo" later — the bar/coin must reflect only what they're doing at the snapshot instant, so they never
  // show both a blue AND an orange state. For this planning/simulation tab that instant is 7:00a LOCAL on the
  // selected day. (On a live board the equivalent snapshot would be the current wall-clock time.)
  const SNAPSHOT_LOCAL_HHMM = '07:00';
  const snapshotMs = new Date(`${selectedDate}T${SNAPSHOT_LOCAL_HHMM}:00`).getTime();

  // A single case in this doctor's room(s) that needs a nerve block: which OR, which site, when the case
  // starts (that's when they'd need to go do the block), and the surgery type.
  type NerveBlockCase = { roomLabel: string; hospitalId?: string; startTimeUtc: string; surgeryType?: string };

  // Cases in a room that was already running: whoever staffs one takes the room over and needs no report-in lead.
  const takeovers = takeoverScheduleIds(allocations, sid => scheduleById.get(sid)?.operatingRoomId,
    sid => { const st = scheduleById.get(sid)?.startTimeUtc; return st ? Date.parse(st) : undefined; });
  const facts = (id: string) => {
    const sites = new Set<string>();
    let earliestStartUtc: string | undefined;
    let earliestNerveBlock = false;
    let earliestHasNurse: boolean | undefined;   // staffing of their FIRST case (the 7:00a snapshot fallback)
    const inCases: { startMs: number; nerveBlock: boolean; takingOver: boolean }[] = [];   // -> the derived report-in
    // 7:00a SNAPSHOT counts: how many CRNA rooms (supervising) vs no-CRNA rooms (solo) are running at 7:00a?
    let snapCrna = 0, snapSolo = 0, activeAtSnapshot = false;
    const nerveBlocks: NerveBlockCase[] = [];
    // Per-room case detail for the popover's Rooms section (OR -> its cases, each with a start time).
    const roomsMap = new Map<string, { operatingRoomId: string; roomLabel: string; startTimeUtc: string; surgeryTypes: string[]; hasCrna: boolean; cases: { startTimeUtc: string; surgeryType: string }[] }>();
    for (const a of allocations) {
      if (!effectiveDoctorIds(a).includes(id)) continue;
      const sched = scheduleById.get(a.operatingRoomScheduleId);
      const hasNurse = effectiveNurseIds(a as AllocationLike).length > 0;
      if (sched) {
        const room = roomById.get(sched.operatingRoomId);
        const label = room?.name || room?.roomNumber || 'OR';
        if (room) sites.add(room.hospitalId);
        if (!earliestStartUtc || sched.startTimeUtc < earliestStartUtc) {
          earliestStartUtc = sched.startTimeUtc;
          earliestNerveBlock = needsNerveBlock(sched.surgeryType);
          earliestHasNurse = hasNurse;
        }
        inCases.push({ startMs: Date.parse(sched.startTimeUtc), nerveBlock: needsNerveBlock(sched.surgeryType), takingOver: takeovers.has(a.operatingRoomScheduleId) });
        // Is THIS case the one running at the 7:00a snapshot instant (start ≤ 7:00a < end)?
        const st = Date.parse(sched.startTimeUtc), en = Date.parse(sched.endTimeUtc);
        if (Number.isFinite(st) && Number.isFinite(en) && snapshotMs >= st && snapshotMs < en) {
          activeAtSnapshot = true;
          if (hasNurse) snapCrna++; else snapSolo++;
        }
        // Accumulate the per-room case list.
        const entry = roomsMap.get(sched.operatingRoomId) || { operatingRoomId: sched.operatingRoomId, roomLabel: label, startTimeUtc: sched.startTimeUtc, surgeryTypes: [], hasCrna: false, cases: [] };
        entry.cases.push({ startTimeUtc: sched.startTimeUtc, surgeryType: sched.surgeryType || '' });
        if (sched.surgeryType && !entry.surgeryTypes.includes(sched.surgeryType)) entry.surgeryTypes.push(sched.surgeryType);
        if (hasNurse) entry.hasCrna = true;
        if (sched.startTimeUtc < entry.startTimeUtc) entry.startTimeUtc = sched.startTimeUtc;
        roomsMap.set(sched.operatingRoomId, entry);
        // Collect EVERY nerve-block case in their rooms (not just the earliest) so the chip can list them all.
        if (needsNerveBlock(sched.surgeryType)) {
          nerveBlocks.push({ roomLabel: label, hospitalId: room?.hospitalId, startTimeUtc: sched.startTimeUtc, surgeryType: sched.surgeryType });
        }
      }
    }
    // Nothing running exactly at 7:00a (e.g. their first case starts 7:20a)? Fall back to the FIRST case of
    // the day — the role they're reporting in for at the top of the morning.
    if (!activeAtSnapshot && earliestHasNurse !== undefined) {
      if (earliestHasNurse) snapCrna = 1; else snapSolo = 1;
    }
    // One place per instant: supervising (a CRNA case is running) wins over solo in the degenerate overlap.
    // (This 7:00a snapshot drives ONLY the tab's left-edge bar.)
    const supervising = snapCrna > 0;
    const solo = snapCrna === 0 && snapSolo > 0;
    // Supervision for the person card — computed the SAME way as the Board so the signature dots (and the
    // Supervising/In-room coin) match whether the card is opened here or on the Board. crnaRooms/soloRooms
    // are per PHYSICAL ROOM. The dot count is the "at the moment" load: PEAK concurrent signatures (rooms
    // going now or starting within the hour), not the whole-day total — so a doctor who relieves into
    // afternoon rooms isn't shown running them all at once. Same math as the Board's drop warning.
    let wdSupervisedRooms = 0, wdSoloRooms = 0;
    for (const r of roomsMap.values()) { if (r.hasCrna) wdSupervisedRooms++; else wdSoloRooms++; }
    const sigUsed = peakConcurrentSignatures(id, allocations, {
      roomIdOf: (sid) => scheduleById.get(sid)?.operatingRoomId,
      startMsOf: (sid) => { const s = scheduleById.get(sid)?.startTimeUtc; return s ? Date.parse(s) : undefined; },
      endMsOf: (sid) => { const e = scheduleById.get(sid)?.endTimeUtc; return e ? Date.parse(e) : undefined; },
      isNurseLike: (nid) => { const t = empTypeById.get(nid) || ''; return t === 'nurse' || t.startsWith('nurse-student'); },
      doctorIdsOf: (a) => effectiveDoctorIds(a as AllocationLike),
      nurseIdsOf: (a) => effectiveNurseIds(a as AllocationLike),
    });
    const supervision = { totalRooms: roomsMap.size, crnaRooms: wdSupervisedRooms, greenStudentRooms: 0, otherStudentRooms: 0, soloRooms: wdSoloRooms, signaturesUsed: sigUsed, concurrentRooms: sigUsed };
    const assignedRooms = [...roomsMap.values()]
      .map(r => ({ ...r, cases: r.cases.slice().sort((a, b) => a.startTimeUtc.localeCompare(b.startTimeUtc)) }))
      .sort((a, b) => a.startTimeUtc.localeCompare(b.startTimeUtc));
    // Earliest case first — the most time-pressured block to go place is at the top.
    nerveBlocks.sort((a, b) => a.startTimeUtc.localeCompare(b.startTimeUtc));
    let reportInHM: string | undefined;
    // 30 min before a room they open (45 for a nerve block); a room they take over, when they take it over — at the case
    // start, or partway through a case at its handoff time (Braden 2026-09-14).
    for (const h of handoffTimesFor(reliefOverlay, id)) if (scheduleById.has(h.scheduleId)) inCases.push({ startMs: h.ms, nerveBlock: false, takingOver: true });
    const inMs = reportInMs(inCases);
    if (inMs !== undefined) reportInHM = utcToLocalTime(new Date(inMs).toISOString());
    let override: string | undefined;
    for (const a of allocations) { const c = a.colorOverrides?.[id]; if (c) { override = c; break; } }
    return { sites: [...sites], reportInHM, earliestNerveBlock, nerveBlocks, supervising, solo, override, allocated: !!earliestStartUtc, supervision, assignedRooms };
  };

  // Left-edge status bars. Supervising vs solo is a 7:00a SNAPSHOT (see facts): a doctor who supervises
  // CRNAs in the morning and sits a room solo later shows ONLY whichever they're doing at 7:00a — the two
  // never stack. Meeting / Request come from the chief's disposition (set on the card). Each label reads
  // the MEANING, with no colour word in it: the palette is user-editable from the Color Legend, so a
  // label that names a colour ("Orange — solo") goes stale the moment someone recolours that category.
  const tagsFor = (id: string, f: ReturnType<typeof facts>): { color: string; label: string }[] => {
    const tags: { color: string; label: string }[] = [];
    const sts = anno.status[id] || [];
    if (sts.includes('meeting')) tags.push({ color: MEETING_COLOR, label: 'Has a meeting today' });
    if (sts.includes('request') || matchesPaletteColor('request', f.override)) tags.push({ color: NAMEPLATE_COLORS.request, label: 'Request case' });
    if (f.supervising) tags.push({ color: NAMEPLATE_COLORS.docSupervise, label: 'Supervising a CRNA (at 7:00a)' });
    else if (f.solo || matchesPaletteColor('docSolo', f.override)) tags.push({ color: NAMEPLATE_COLORS.docSolo, label: 'Solo in a room, no CRNA (at 7:00a)' });
    return tags;
  };

  // ---- Ordering (mirrors the bullpen) ----
  const byLastName = (a: EmployeeLike, b: EmployeeLike) => a.lastName.localeCompare(b.lastName);
  const fixedDocs = doctors.filter(e => orderOf(e.employeeId) === null);
  const peckingDocs = doctors.filter(e => orderOf(e.employeeId) !== null)
    .sort((a, b) => (orderOf(a.employeeId)! - orderOf(b.employeeId)!) || byLastName(a, b));

  // Drag a pecking-order doc onto another: renumber the whole pecking list 1..N by the new positions.
  const reorderPecking = (draggedId: string, targetId: string) => {
    const order = reorderedPeckingIds(peckingDocs.map(e => e.employeeId), draggedId, targetId);
    if (!order) return;
    // MERGE, never replace: `order` is the SHARED oc-anes map and the CRNA tab now numbers its own pecking list in
    // it too. Rebuilding it from this tab's doctor ids alone would silently wipe every CRNA's number on the first
    // doctor drag. Doctors are unaffected — peckingDocs already covers every numbered doctor, so their entries are
    // all rewritten here anyway.
    mutateAnno(p => ({ ...p, order: { ...p.order, ...order } }));
  };

  const windowLabel = (id: string): string => {
    const s = shiftByEmp.get(id);
    return s && s.startTimeUtc && s.endTimeUtc ? `${to12h(localHM(s.startTimeUtc))}–${to12h(localHM(s.endTimeUtc))}` : 'No shift';
  };
  const fixedGroups = (() => {
    const groups = new Map<string, { label: string; startMin: number; endMin: number; items: EmployeeLike[] }>();
    for (const e of fixedDocs) {
      const lbl = windowLabel(e.employeeId);
      const s = shiftByEmp.get(e.employeeId);
      if (!groups.has(lbl)) groups.set(lbl, { label: lbl, startMin: localMin(s?.startTimeUtc), endMin: localMin(s?.endTimeUtc), items: [] });
      groups.get(lbl)!.items.push(e);
    }
    return [...groups.values()].sort((a, b) => a.endMin - b.endMin || a.startMin - b.startMin);
  })();

  // ---- Name search (display only) ----
  // Filtering narrows what is SHOWN; ordering, numbering and every edit still act on the full lists above.
  // Drag-reordering is disabled while filtering, because dropping between two visible rows would silently
  // reorder people the search is hiding.
  const filtering = nameFilter.trim().length > 0;
  const shownByName = (e: EmployeeLike) => matchesNameQuery(e, nameFilter);
  const visibleFixedDocs = fixedDocs.filter(shownByName);
  const visiblePeckingDocs = peckingDocs.filter(shownByName);
  const visibleFixedGroups = fixedGroups
    .map(g => ({ ...g, items: g.items.filter(shownByName) }))
    .filter(g => g.items.length > 0);

  // ---- Row rendering ----
  // NOTE: these are plain render FUNCTIONS invoked as `renderRow(e)`, NOT React components rendered as
  // `<Row/>`. Defining a component inside this body and mounting it as an element gives it a fresh identity
  // every render, so React unmounts/remounts it — the in-time and note <input>s would lose focus after each
  // keystroke and an in-progress drag would abort. Inlining the returned JSX keeps element identity stable.
  // They stay in the body (not module scope) because they close over facts/anno/setInTime/hospitalById/etc.
  // The "coin": a small badge with the site acronym, colored to match the hospital they ended up at
  // (the hospital's own editable color from the Hospitals page, same source the Board uses).
  const renderSitePill = (hospitalId: string): JSX.Element => {
    const h = hospitalById.get(hospitalId);
    const name = h?.name || '';
    const color = colorForHospital(h);
    return <span key={hospitalId} title={name} style={{ fontSize: '0.7em', fontWeight: 800, letterSpacing: '0.3px', padding: '1px 7px', borderRadius: '10px', background: color, color: '#fff', border: `1px solid ${darkenHex(color, 0.2)}`, textShadow: '0 1px 1px rgba(0,0,0,0.35)', whiteSpace: 'nowrap' }}>{siteTag(name)}</span>;
  };

  // The clickable site "coin". Click it to open a floating, SCROLLABLE list of every site as a color-coded
  // coin (rendered as a body portal so the section's overflow can't clip it). The face shows a small lock +
  // the locked site's coin if a hard lock is set, else the coin(s) of the site(s) they were allocated at,
  // else a dashed "lock site" hint. Always active — you don't need to unlock the list to lock a site here.
  const renderSiteCoin = (id: string, sites: string[]): JSX.Element => {
    const lockHid = siteLocks[id];
    const lockColor = lockHid ? colorForHospital(hospitalById.get(lockHid)) : '';
    return (
      <button type="button"
        onClick={(ev) => { ev.stopPropagation(); const r = ev.currentTarget.getBoundingClientRect(); setSiteLockOpen(prev => (prev && prev.id === id) ? null : { id, top: r.bottom + 4, left: r.left }); }}
        title="Click to lock this anesthesiologist to a site for the day — used only there on the next Allocate run"
        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', background: 'transparent', border: 0, padding: 0 }}>
        {lockHid && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '0.7em', fontWeight: 800, letterSpacing: '0.3px', padding: '1px 7px', borderRadius: '10px',
            background: lockColor, color: '#fff', border: `1px solid ${darkenHex(lockColor, 0.2)}`, textShadow: '0 1px 1px rgba(0,0,0,0.35)', whiteSpace: 'nowrap' }}>
            <span aria-hidden style={{ fontSize: '0.9em' }}>🔒</span>{siteTag(hospitalNameById.get(lockHid))}
          </span>
        )}
        {sites.map(hid => renderSitePill(hid))}
        {!lockHid && sites.length === 0 && (
          <span style={{ fontSize: '0.72em', fontWeight: 700, color: 'var(--text-muted)', padding: '1px 7px', borderRadius: '10px',
            border: '1px dashed var(--border-subtle)', whiteSpace: 'nowrap' }}>lock site ▾</span>
        )}
      </button>
    );
  };

  const renderRow = (e: EmployeeLike, badge?: string, draggable?: boolean): JSX.Element => {
    const id = e.employeeId;
    const f = facts(id);
    const s = shiftByEmp.get(id);
    // In-time is the DERIVED report-in: 30 min before a room the doctor opens (45 if it
    // needs a nerve block), or the case start of a room they take over, computed in facts(). Shift-work doctors have
    // a fixed window (06:00 availability → 15:00/17:00 leave), so until they're
    // allocated we fall back to their availability start; once allocated the derived
    // report-in wins (e.g. earliest room 07:00 → arrive 06:30). Only shift-work doctors
    // have a fixed OUT-time (their leave); pecking-order docs stay until the board clears.
    const isShiftWork = orderOf(id) === null;
    const hasFixedWindow = !!(s && s.startTimeUtc && s.endTimeUtc && localMin(s.startTimeUtc) !== localMin(s.endTimeUtc));
    // In-time = 30 min before a room they open (45 for a nerve block), or the case start of a room they take over — for EVERY
    // doctor, shift-work and pecking-order alike (Braden 08-15). Only a doctor with NO assigned case yet falls
    // back: a shift-work doc to their availability (shift) start, an un-needed pecking doc to the 11:30 bench.
    const computedInHM = f.reportInHM || (hasFixedWindow ? localHM(s!.startTimeUtc) : (!isShiftWork ? BENCH_IN_HM : ''));
    const inHM = anno.inTime[id] || computedInHM;
    const isInOverride = !!anno.inTime[id];
    // Roller options: the 15-min grid, plus the current in-time itself if it lands off-grid (e.g. an
    // auto report-in of 06:35) so the select always shows the real value.
    const inOptions = inHM && !IN_TIME_GRID.includes(inHM) ? [inHM, ...IN_TIME_GRID] : IN_TIME_GRID;
    const outHM = (isShiftWork && hasFixedWindow) ? localHM(s!.endTimeUtc) : '';
    const tags = tagsFor(id, f);
    const note = anno.notes[id] || '';
    const sentHome = (anno.status[id] || []).includes('off');
    const canDrag = !!(draggable && !locked);
    const isDragTarget = dragId && dragId !== id && draggable;

    return (
      <div
        key={id}
        draggable={canDrag}
        onDragStart={canDrag ? (ev) => { setDragId(id); ev.dataTransfer.effectAllowed = 'move'; } : undefined}
        onDragEnd={canDrag ? () => setDragId(null) : undefined}
        onDragOver={isDragTarget ? (ev) => { ev.preventDefault(); ev.dataTransfer.dropEffect = 'move'; } : undefined}
        onDrop={isDragTarget ? (ev) => { ev.preventDefault(); if (dragId) reorderPecking(dragId, id); setDragId(null); } : undefined}
        style={{ borderBottom: '1px solid var(--border-subtle)', background: dragId === id ? 'var(--bg-surface-raised)' : 'var(--bg-surface)', opacity: dragId === id ? 0.6 : (sentHome ? 0.45 : 1), filter: sentHome ? 'grayscale(0.6)' : undefined }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px' }}>
          {/* LEFT CLUSTER — fixed width so the site coin sits in the SAME vertical column on every row and
              across BOTH sections (Shift + Pecking), regardless of name length, how many status bars show,
              or whether an OUT-time / pecking-number badge is present. The name flexes to absorb the slack,
              so the coin that follows is always pinned to the same x. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '272px', flexShrink: 0 }}>
            {/* Drag handle — reserved on EVERY row (glyph shows only on draggable pecking rows) so names and
                everything after line up across both sections too. */}
            <span
              style={{ width: '12px', flexShrink: 0, textAlign: 'center', fontSize: '0.9em', cursor: canDrag ? 'grab' : 'default', color: canDrag ? 'var(--text-muted)' : 'var(--text-faint)' }}
              title={draggable ? (canDrag ? 'Drag to reorder' : 'Unlock to reorder') : undefined}
            >{draggable ? '⠿' : ''}</span>
            {/* Left-edge status bars — a doctor can have several at once. Each bar is a thin 4px sliver
                wrapped in a padded, transparent hit-area with cursor:help, so hovering anywhere near it
                reliably surfaces the "Color — meaning" tooltip (the bare 4px bar is nearly impossible to
                hover). */}
            <div style={{ display: 'flex', gap: '0px', flexShrink: 0 }}>
              {tags.length === 0
                ? <span style={{ width: '10px', height: '22px' }} />
                : tags.map((t, i) => (
                    <span key={i} title={t.label} style={{ display: 'inline-flex', alignItems: 'center', padding: '0 3px', cursor: 'help' }}>
                      <span style={{ width: '4px', height: '22px', borderRadius: '2px', background: t.color }} />
                    </span>
                  ))}
            </div>
            {/* OUT time (shift docs only) — stacked "OUT / 5:00p" block to the LEFT of the name. */}
            {outHM && (
              <div style={{ flexShrink: 0, textAlign: 'center', minWidth: '46px', lineHeight: 1.05 }} title="Shift end (out time)">
                <div style={{ fontSize: '0.52em', fontWeight: 700, letterSpacing: '0.6px', color: 'var(--text-faint)' }}>OUT</div>
                <div style={{ fontSize: '0.82em', fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{to12h(outHM)}</div>
              </div>
            )}
            {badge && <span style={{ fontSize: '0.78em', fontWeight: 800, color: 'var(--amber)', minWidth: '20px', textAlign: 'center', flexShrink: 0 }}>{badge}</span>}
            {/* Click the name to open the card. Flexes to fill the cluster (minWidth:0 lets it shrink) so the
                coin never shifts with name length. No underline — clickability reads from the color + cursor. */}
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <EmployeeDetailsPopover
                employee={e as unknown as Employee}
                shift={s && s.startTimeUtc && s.endTimeUtc ? { startTimeUtc: s.startTimeUtc, endTimeUtc: s.endTimeUtc, order: orderOf(id) ?? undefined } : undefined}
                hospitalName={e.homeHospitalId ? hospitalNameById.get(e.homeHospitalId) : undefined}
                supervision={f.supervision}
                assignedRooms={f.assignedRooms}
                atSites={f.sites.map(coinFor)}
                credentialSites={credentialCoinsFor(e)}
                inTimeLabel={inHM ? to12h(inHM) : undefined}
                statusControl={{ statuses: anno.status[id] || [], note, onToggleStatus: (st) => toggleStatus(id, st), onSetNote: (t) => setNote(id, t), offEntries: anno.offLog[id], onSetOffTime: (i, field, hm) => setOffTime(id, i, field, hm) }}
                validSiteNames={hospitalNames}
                boardActivityCoin
                recolor={{ current: f.override, onPick: (c) => handleRecolor(id, c) }}
                roomTransfer={{
                  candidates: allCandidatesFor(id),
                  candidateGroups: groupTransferCandidates(
                    transferTargetDoctorIds.filter((t: string) => t !== id), assignedDoctorsBySite, nameOfDoc,
                    (hid: string) => hospitalNameById.get(hid) || '', undefined,
                    (hid: string) => { const cc = coinFor(hid); return { color: cc.color, acronym: cc.acronym }; },
                    (cid: string) => orderOf(cid),
                  ),
                  candidatesForRoom: (rid) => roomCandidatesFor(rid, id),
                  onMove: (roomId, toId) => moveRoom(roomId, toId),
                  onMoveAll: (toId) => moveAllRooms(id, toId),
                  onSwapAll: (otherId) => swapAllRooms(id, otherId),
                  onSwapRoom: (roomId, otherId) => swapRoom(roomId, id, otherId),
                  getCandidateCard: (cid: string) => {
                    const ce = employees.find(x => x.employeeId === cid);
                    if (!ce) return undefined;
                    const cf = facts(cid);
                    const cs = shiftByEmp.get(cid);
                    return {
                      employee: ce as unknown as Employee,
                      shift: cs && cs.startTimeUtc && cs.endTimeUtc ? { startTimeUtc: cs.startTimeUtc, endTimeUtc: cs.endTimeUtc, order: orderOf(cid) ?? undefined } : undefined,
                      hospitalName: ce.homeHospitalId ? hospitalNameById.get(ce.homeHospitalId) : undefined,
                      supervision: cf.supervision,
                      assignedRooms: cf.assignedRooms,
                      atSites: cf.sites.map(coinFor),
                      credentialSites: credentialCoinsFor(ce),
                      inTimeLabel: cf.reportInHM ? to12h(cf.reportInHM) : undefined,
                      validSiteNames: hospitalNames,
                    };
                  },
                }}
                triggerStyle={{ display: 'inline-block', maxWidth: '100%', textDecoration: 'none' }}
              >
                <span style={{ fontWeight: 600, fontSize: '0.9em', cursor: 'pointer', whiteSpace: 'nowrap' }}>{e.firstName} {e.lastName}</span>
              </EmployeeDetailsPopover>
            </div>
          </div>
          {/* Site coin (now pinned to a fixed x by the cluster above). Click to lock to any site. */}
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', flexShrink: 0, alignItems: 'center' }}>
            {renderSiteCoin(id, f.sites)}
          </div>
          {/* IN time — right beside the coin. Auto-derived from assignment; click to set manually on a
              15-min roller (05:00–23:00). "Auto" clears the override back to the derived report-in. */}
          <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
            title="In time (report-in) — auto from assignment; click to set manually (15-min steps)">
            <span style={{ fontSize: '0.5em', fontWeight: 700, letterSpacing: '0.6px', color: 'var(--text-faint)', marginRight: '1px' }}>IN</span>
            <select
              value={inHM}
              onChange={ev => setInTime(id, ev.target.value)}
              style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', border: '1px solid transparent', borderRadius: '5px', background: 'transparent', cursor: 'pointer', fontSize: '0.84em', fontWeight: 700, color: isInOverride ? 'var(--or-blue-mid)' : (inHM ? 'var(--text-primary)' : 'var(--text-faint)'), padding: '1px 3px' }}
              onMouseEnter={ev => (ev.currentTarget.style.borderColor = 'var(--border-soft)')}
              onMouseLeave={ev => (ev.currentTarget.style.borderColor = 'transparent')}
            >
              <option value="">⟳ Auto{computedInHM ? ` (${to12h(computedInHM)})` : ''}</option>
              {inOptions.map(t => <option key={t} value={t}>{to12h(t)}</option>)}
            </select>
          </span>
          {/* Sent-home badge — the whole row is dimmed/greyed too; the name stays clickable to bring them back. */}
          {sentHome && (
            <span style={{ fontSize: '0.58em', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', background: OFF_COLOR, padding: '2px 8px', borderRadius: '10px', flexShrink: 0 }}
              title="Sent home — click the name to call them back in">Sent home</span>
          )}
          {/* Nerve-block chip — shows whenever ANY case in their room(s) needs a block. Click to expand a
              compact detail list INLINE, filling the empty space to the right (which room, which case, when
              it starts). */}
          {f.nerveBlocks.length > 0 && (
            <button
              type="button"
              draggable={false}
              aria-expanded={nbOpenIds.has(id)}
              aria-controls={`nb-panel-${id}`}
              onClick={() => toggleNbOpen(id)}
              title="Click to see which room(s) and case(s) need a nerve block, and when each case starts"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0,
                fontSize: '0.66em', fontWeight: 800, letterSpacing: '0.2px', textTransform: 'uppercase',
                color: 'var(--amber)', background: 'rgba(217, 119, 6, 0.18)',
                border: `1px solid ${NAMEPLATE_COLORS.nerveBlockBorder}`, borderRadius: '10px', padding: '2px 8px', cursor: 'pointer',
              }}
            >
              Nerve block{f.nerveBlocks.length > 1 ? ` ×${f.nerveBlocks.length}` : ''}
              <span aria-hidden style={{ fontSize: '0.9em', opacity: 0.8 }}>{nbOpenIds.has(id) ? '▴' : '▾'}</span>
            </button>
          )}
          {/* When expanded, the block details fill the empty space to the RIGHT of the chip as a compact,
              small-font list (one pill per case: room · CASE START time · surgery). Collapsed = plain spacer. */}
          {f.nerveBlocks.length > 0 && nbOpenIds.has(id) ? (() => {
            // Only count real, distinct sites — a case whose room is missing has no hospitalId and must not
            // flip on the per-pill site tag for an otherwise single-site doctor.
            const nbMultiSite = new Set(f.nerveBlocks.map(nb => nb.hospitalId).filter(Boolean)).size > 1;
            return (
              <div id={`nb-panel-${id}`} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 7px', paddingLeft: '8px' }}>
                {f.nerveBlocks.map((nb, i) => (
                  <span key={i} title={`${nb.roomLabel} — case start ${to12h(localHM(nb.startTimeUtc))}${nb.surgeryType ? ` — ${nb.surgeryType}` : ''}`}
                    style={{ display: 'inline-flex', alignItems: 'baseline', gap: '5px', whiteSpace: 'nowrap', fontSize: '0.62em',
                      background: 'rgba(217, 119, 6, 0.10)', border: `1px solid ${NAMEPLATE_COLORS.nerveBlockBorder}`, borderRadius: '9px', padding: '1px 8px', maxWidth: '100%' }}>
                    <span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>
                      {nb.roomLabel}{nbMultiSite && nb.hospitalId ? ` · ${siteTag(hospitalNameById.get(nb.hospitalId))}` : ''}
                    </span>
                    <span style={{ fontWeight: 800, letterSpacing: '0.4px', color: 'var(--amber)', textTransform: 'uppercase' }}>start</span>
                    <span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{to12h(localHM(nb.startTimeUtc))}</span>
                    {nb.surgeryType && <span style={{ color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nb.surgeryType}</span>}
                  </span>
                ))}
              </div>
            );
          })() : (
            <div style={{ flex: 1 }} />
          )}
        </div>
        {/* Notes bar under the name (also fed by the Meeting/Request notes typed on the card). */}
        {locked ? (
          note ? <div style={{ padding: '0 10px 6px 34px', fontSize: '0.76em', color: 'var(--text-secondary)', fontStyle: 'italic' }}>{note}</div> : null
        ) : (
          <div style={{ padding: '0 10px 6px 34px' }}>
            <input value={note} onChange={ev => setNote(id, ev.target.value)} placeholder="Add a note…" className="form-control"
              style={{ fontSize: '0.76em', padding: '2px 6px', width: '100%', maxWidth: '460px' }} />
          </div>
        )}
      </div>
    );
  };

  const renderSection = (title: string, count: number, headColor: string, children: React.ReactNode): JSX.Element => (
    <div style={{ marginBottom: '16px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${headColor}55` }}>
      <div style={{ background: headColor, color: '#fff', fontWeight: 700, fontSize: '0.78em', letterSpacing: '0.4px', textTransform: 'uppercase', padding: '6px 10px', display: 'flex', justifyContent: 'space-between' }}>
        <span>{title}</span><span style={{ opacity: 0.9 }}>{count}</span>
      </div>
      <div>{children}</div>
    </div>
  );

  return (
    <div style={{ padding: '8px 4px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {!embedded && <h1 style={{ margin: 0, fontSize: '26px', fontFamily: 'var(--font-display)', fontWeight: 700 }}>Anesthesiologists</h1>}
        {!embedded && <input type="date" aria-label="Date" className="form-control" style={{ maxWidth: '180px' }} value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />}
        <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>{doctors.length} working</span>
        <DataFreshnessNote refreshing={day.refreshing} refreshFailed={day.refreshFailed} onRetry={() => { void day.refetchAll(); }} />
        {/* Undo the last persisted move/swap (Option B: moves rewrite the allocation; this reverses them). */}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={undoStack.length === 0}
          onClick={undoLastMove}
          title={undoStack.length ? `Undo: ${undoStack[undoStack.length - 1].label}` : 'Nothing to undo'}
        >↩ Undo{undoStack.length > 1 ? ` (${undoStack.length})` : ''}</button>
        {/* Bulk-clear manual in-time overrides -> every doctor back to their derived (auto) report-in. */}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={locked || !doctors.some(d => anno.inTime[d.employeeId])}
          onClick={resetInTimes}
          title="Reset all in-times to Auto — clears every manual in-time override so each doctor falls back to their report-in (30 min before their first case). Use after a re-sim or to undo hand-laddered in-times."
        >⟲ In-times → Auto</button>
        {/* Lock / unlock toggle — hidden for read-only accounts, which can never unlock. */}
        {isAdmin && <LockToggle locked={locked} onChange={(nextLocked) => (nextLocked ? lockNow() : unlock())} />}
      </div>

      {!isAdmin && <ReadOnlyBanner />}
      {(error || loadError) && <div role="alert" style={{ color: 'var(--danger-text, #dc2626)', marginBottom: '10px' }}>{error || loadError}</div>}
      {loading ? (
        <div style={{ color: 'var(--text-muted)' }}>Loading…</div>
      ) : doctors.length === 0 ? (
        <div style={{ color: 'var(--text-muted)' }}>No anesthesiologists have shifts on this day.</div>
      ) : visibleFixedDocs.length + visiblePeckingDocs.length === 0 ? (
        <div style={{ color: 'var(--text-muted)' }}>No anesthesiologists match “{nameFilter.trim()}”.</div>
      ) : (
        <>
          {visibleFixedDocs.length > 0 && renderSection('Shift', visibleFixedDocs.length, NAMEPLATE_COLORS.docOut5pm, (
            <>
              {visibleFixedGroups.map(g => (
                <div key={g.label}>
                  <div style={{ fontSize: '0.66em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', color: 'var(--text-faint)', padding: '5px 10px 2px', background: 'var(--bg-surface-raised)' }}>{g.label}</div>
                  {g.items.slice().sort(byLastName).map(e => renderRow(e))}
                </div>
              ))}
            </>
          ))}

          {(visiblePeckingDocs.length > 0 || !filtering) && renderSection('Pecking Order', visiblePeckingDocs.length, NAMEPLATE_COLORS.peckingDoctor, (
            visiblePeckingDocs.length === 0
              ? <div style={{ padding: '8px 10px', fontSize: '0.8em', color: 'var(--text-faint)' }}>No pecking-order anesthesiologists today.</div>
              // The pecking number is the position in the FULL list, so a search never renumbers anyone.
              : visiblePeckingDocs.map(e => renderRow(e, `${peckingDocs.indexOf(e) + 1}`, !filtering))
          ))}

          <div style={{ fontSize: '0.72em', color: 'var(--text-faint)', marginTop: '4px' }}>
            Unlock to drag pecking-order docs (order mirrors into the Board bullpen), edit in-times, and add notes. Set Meeting / Request case / Off (sent home) from each doctor's card. Changes save locally and auto-lock after 30 seconds.
          </div>
        </>
      )}

      {/* Floating, scrollable site-lock picker — a body portal so the section's overflow can't clip it. */}
      {siteLockOpen && (() => {
        const slo = siteLockOpen;
        return ReactDOM.createPortal((
          <>
            <div onMouseDown={() => setSiteLockOpen(null)} style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'transparent' }} />
            <div style={{ position: 'fixed', top: `${slo.top}px`, left: `${slo.left}px`, zIndex: 100001, background: 'var(--popover-bg)', border: '1px solid var(--popover-border)', borderRadius: '8px', boxShadow: 'var(--shadow-lg)', padding: '6px', width: '230px', maxHeight: '280px', overflowY: 'auto' }}>
              <div style={{ fontSize: '0.64em', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-faint)', padding: '2px 6px 6px' }}>Lock to a site for the day</div>
              <button type="button" onClick={() => { setLock(slo.id, null); setSiteLockOpen(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', padding: '5px 6px', borderRadius: '6px', cursor: 'pointer', background: 'transparent', border: '1px solid transparent', color: 'var(--text-secondary)' }}
                onMouseEnter={ev => (ev.currentTarget.style.background = 'var(--bg-surface-raised)')}
                onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}>
                <span style={{ fontSize: '0.68em', fontWeight: 700, color: 'var(--text-muted)', padding: '1px 7px', borderRadius: '10px', border: '1px dashed var(--border-subtle)', flexShrink: 0 }}>none</span>
                <span style={{ fontSize: '0.82em' }}>No site lock</span>
              </button>
              {hospitals.map(h => {
                const color = colorForHospital(h);
                const active = siteLocks[slo.id] === h.hospitalId;
                return (
                  <button key={h.hospitalId} type="button" onClick={() => { setLock(slo.id, h.hospitalId); setSiteLockOpen(null); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', padding: '5px 6px', borderRadius: '6px', cursor: 'pointer', background: active ? 'var(--bg-surface-raised)' : 'transparent', border: '1px solid transparent' }}
                    onMouseEnter={ev => (ev.currentTarget.style.background = 'var(--bg-surface-raised)')}
                    onMouseLeave={ev => (ev.currentTarget.style.background = active ? 'var(--bg-surface-raised)' : 'transparent')}>
                    <span style={{ fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.3px', padding: '1px 7px', borderRadius: '10px', background: color, color: '#fff', border: `1px solid ${darkenHex(color, 0.2)}`, textShadow: '0 1px 1px rgba(0,0,0,0.35)', whiteSpace: 'nowrap', flexShrink: 0 }}>{siteTag(h.name)}</span>
                    <span style={{ flex: 1, fontSize: '0.82em', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.name}</span>
                    {active && <span aria-hidden style={{ fontSize: '0.9em', fontWeight: 800, color }}>✓</span>}
                  </button>
                );
              })}
            </div>
          </>
        ), document.body);
      })()}
    </div>
  );
};

export default AnesthesiologistsView;
